import { useState, useMemo } from "react";

type DataPoint = {
  date: string;
  rate: number;
};

interface CashRateChartProps {
  data: DataPoint[];
}

export function CashRateChart({ data }: CashRateChartProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Chart dimensions
  const width = 800;
  const height = 300;
  const padding = { top: 40, right: 100, bottom: 60, left: 60 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // Calculate scales
  const { minRate, maxRate, points, linePath, areaPath } = useMemo(() => {
    const rates = data.map((d) => d.rate);
    const min = Math.floor(Math.min(...rates) * 10) / 10 - 0.3;
    const max = Math.ceil(Math.max(...rates) * 10) / 10 + 0.3;

    // Calculate points
    const pts = data.map((d, i) => ({
      x: padding.left + (i / (data.length - 1)) * chartWidth,
      y: padding.top + chartHeight - ((d.rate - min) / (max - min)) * chartHeight,
      ...d,
    }));

    // Create smooth curve path using cardinal spline
    const tension = 0.3;
    let path = `M ${pts[0].x} ${pts[0].y}`;

    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[Math.max(0, i - 1)];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[Math.min(pts.length - 1, i + 2)];

      const cp1x = p1.x + ((p2.x - p0.x) * tension) / 3;
      const cp1y = p1.y + ((p2.y - p0.y) * tension) / 3;
      const cp2x = p2.x - ((p3.x - p1.x) * tension) / 3;
      const cp2y = p2.y - ((p3.y - p1.y) * tension) / 3;

      path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
    }

    // Area path (for gradient fill)
    const area = `${path} L ${pts[pts.length - 1].x} ${padding.top + chartHeight} L ${pts[0].x} ${padding.top + chartHeight} Z`;

    return {
      minRate: min,
      maxRate: max,
      points: pts,
      linePath: path,
      areaPath: area,
    };
  }, [data, chartWidth, chartHeight]);

  // Y-axis ticks
  const yTicks = useMemo(() => {
    const ticks = [];
    const step = 0.5;
    for (let rate = Math.ceil(minRate / step) * step; rate <= maxRate; rate += step) {
      ticks.push({
        rate,
        y: padding.top + chartHeight - ((rate - minRate) / (maxRate - minRate)) * chartHeight,
      });
    }
    return ticks;
  }, [minRate, maxRate, chartHeight]);

  return (
    <div className="w-full">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-auto"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Gradient Definitions */}
        <defs>
          <linearGradient id="rateGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.3" />
            <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.02" />
          </linearGradient>
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.6" />
            <stop offset="50%" stopColor="hsl(var(--primary))" stopOpacity="1" />
            <stop offset="100%" stopColor="hsl(142 76% 36%)" stopOpacity="1" />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Background Grid */}
        <g className="text-muted-foreground/20">
          {yTicks.map((tick) => (
            <line
              key={tick.rate}
              x1={padding.left}
              y1={tick.y}
              x2={width - padding.right}
              y2={tick.y}
              stroke="currentColor"
              strokeDasharray="4,4"
            />
          ))}
        </g>

        {/* Y-Axis Labels */}
        <g className="text-muted-foreground text-xs">
          {yTicks.map((tick) => (
            <text
              key={tick.rate}
              x={padding.left - 12}
              y={tick.y + 4}
              textAnchor="end"
              fill="currentColor"
              className="text-[11px]"
            >
              {tick.rate.toFixed(1)}%
            </text>
          ))}
        </g>

        {/* X-Axis Labels */}
        <g className="text-muted-foreground">
          {points.map((point, i) => (
            <text
              key={i}
              x={point.x}
              y={height - padding.bottom + 24}
              textAnchor="middle"
              fill="currentColor"
              className="text-[11px]"
            >
              {point.date}
            </text>
          ))}
        </g>

        {/* Gradient Area Fill */}
        <path
          d={areaPath}
          fill="url(#rateGradient)"
          className="transition-opacity duration-300"
        />

        {/* Line */}
        <path
          d={linePath}
          fill="none"
          stroke="url(#lineGradient)"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#glow)"
          className="transition-all duration-300"
        />

        {/* Data Points */}
        {points.map((point, i) => (
          <g
            key={i}
            onMouseEnter={() => setHoveredIndex(i)}
            onMouseLeave={() => setHoveredIndex(null)}
            className="cursor-pointer"
          >
            {/* Hover area */}
            <circle
              cx={point.x}
              cy={point.y}
              r="20"
              fill="transparent"
            />

            {/* Outer ring (always visible on last point) */}
            <circle
              cx={point.x}
              cy={point.y}
              r={hoveredIndex === i || i === points.length - 1 ? 12 : 0}
              fill="hsl(var(--primary))"
              fillOpacity="0.1"
              className="transition-all duration-200"
            />

            {/* Point */}
            <circle
              cx={point.x}
              cy={point.y}
              r={hoveredIndex === i ? 7 : i === points.length - 1 ? 6 : 5}
              fill={i === points.length - 1 ? "hsl(142 76% 36%)" : "hsl(var(--primary))"}
              stroke="white"
              strokeWidth="2"
              className="transition-all duration-200"
              filter={hoveredIndex === i ? "url(#glow)" : undefined}
            />

            {/* Tooltip */}
            {hoveredIndex === i && (
              <g>
                {/* Tooltip background */}
                <rect
                  x={point.x - 45}
                  y={point.y - 48}
                  width="90"
                  height="36"
                  rx="8"
                  fill="hsl(var(--primary))"
                  className="drop-shadow-lg"
                />
                {/* Tooltip arrow */}
                <polygon
                  points={`${point.x - 6},${point.y - 12} ${point.x + 6},${point.y - 12} ${point.x},${point.y - 4}`}
                  fill="hsl(var(--primary))"
                />
                {/* Tooltip text */}
                <text
                  x={point.x}
                  y={point.y - 32}
                  textAnchor="middle"
                  fill="white"
                  className="text-xs font-medium"
                >
                  {point.rate.toFixed(2)}%
                </text>
                <text
                  x={point.x}
                  y={point.y - 18}
                  textAnchor="middle"
                  fill="white"
                  fillOpacity="0.8"
                  className="text-[10px]"
                >
                  {point.date}
                </text>
              </g>
            )}
          </g>
        ))}

        {/* Current Rate Label */}
        <g>
          <rect
            x={points[points.length - 1].x + 12}
            y={points[points.length - 1].y - 14}
            width="72"
            height="28"
            rx="14"
            fill="hsl(142 76% 36%)"
            className="drop-shadow-md"
          />
          <text
            x={points[points.length - 1].x + 48}
            y={points[points.length - 1].y + 5}
            textAnchor="middle"
            fill="white"
            className="text-sm font-semibold"
          >
            {data[data.length - 1].rate.toFixed(2)}%
          </text>
        </g>

        {/* Y-Axis Title */}
        <text
          x={16}
          y={height / 2}
          textAnchor="middle"
          fill="currentColor"
          className="text-muted-foreground text-[10px]"
          transform={`rotate(-90, 16, ${height / 2})`}
        >
          Cash Rate (%)
        </text>
      </svg>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 mt-4 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-primary" />
          <span>Historical Rate</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-green-600" />
          <span>Current Rate</span>
        </div>
      </div>
    </div>
  );
}
